import React, { useState } from 'react';
import { ContainerTemplate, Flag } from '@/components';
import { Row, Column } from 'mage-components';
import { branch, compose, renderComponent } from 'recompose';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import withLifecycle from '@hocs/with-lifecycle';
import { components as clientComponents } from 'mage-client';
import { Dropdown, FrameStack, Loader, SelectorLogo, Typography } from '@klarna/bubble-ui';

import { getCountries, getCountryConfig, getRegionConfig } from '@/selectors/options';
import capabilities from '@/selectors/capabilities';
import { getPlatforms as getAllPlatforms } from '@/selectors/platforms';
import { CAPS } from '@/constants';

import '../style.css';

import { Flags } from './Flags';
import { Configuration } from './Configuration';
import { VolumeSeparation } from './VolumeSeparation';
import { Platforms } from './Platforms';
import { Products } from './Products';
import { DocumentRequirements } from './DocumentRequirements';

const enhancedComponentPropTypes = {
  allPlatforms: PropTypes.array.isRequired,
  countries: PropTypes.array.isRequired,
  countryConfig: PropTypes.object.isRequired,
  regionConfig: PropTypes.shape({
    ap: PropTypes.object.isRequired,
    eu: PropTypes.object.isRequired,
    us: PropTypes.object.isRequired,
  }).isRequired,
};

type EnhancedComponentProps = PropTypes.InferProps<typeof enhancedComponentPropTypes>;

const Component: React.FC<EnhancedComponentProps> = ({
  countries,
  countryConfig,
  regionConfig,
  allPlatforms,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string>('eu');

  const currentConfig = {
    ...regionConfig[selectedRegion],
    ...(selectedCountry ? countryConfig[selectedCountry as string] : {}),
  };

  const currentCountry = countries?.find(c => c.key === selectedCountry);

  const countryPlatforms = allPlatforms?.filter(p =>
    p.supportedCountries.includes(selectedCountry),
  );

  return (
    <ContainerTemplate
      contentWidth={12}
      title="Onboarding configuration"
      subtitle="Pick a country and region. If it's a cross border case, ignore the product and platform configuration, those always consider the default region"
    >
      <Row>
        <Column columns={{ mobile: 3 }}>
          <Dropdown
            label="Region"
            onChange={(v: string) => setSelectedRegion(v)}
            value={selectedRegion}
            options={[
              { key: '', label: '', disabled: true, hidden: true },
              { key: 'eu', label: 'EU' },
              { key: 'ap', label: 'AP' },
              { key: 'us', label: 'US' },
            ]}
            defaultValue="eu"
          />
          <SelectorLogo
            onSelect={(c: string) => setSelectedCountry(c)}
            options={countries
              ?.filter(c => c.data.supported)
              .map(c => ({
                key: c.key,
                value: c.key,
                label: c.key,
                pressed: selectedCountry === c.key,
                logoProps: {
                  icon: () => <Flag country={c.key} height={50} width={50} viewBox="0 0 180 180" />,
                },
              }))}
          />
        </Column>
        {selectedCountry && (
          <Column columns={{ mobile: 9 }}>
            <Typography textToken="text-style/headings/titles/bold/secondary">
              {currentCountry.label}
            </Typography>

            <FrameStack>
              <Flags currentConfig={currentConfig} />
              <Configuration currentConfig={currentConfig} />
              {currentConfig.VOLUME_SEPARATION && (
                <VolumeSeparation currentCountry={currentCountry} currentConfig={currentConfig} />
              )}
              <Platforms selectedCountry={selectedCountry} countryPlatforms={countryPlatforms} />
              <Products selectedCountry={selectedCountry} currentConfig={currentConfig} />
              <DocumentRequirements currentConfig={currentConfig} />
            </FrameStack>
          </Column>
        )}
      </Row>
    </ContainerTemplate>
  );
};

Component.propTypes = enhancedComponentPropTypes;

const INIT_CAPS = {
  getOptions: CAPS.V2_OPTIONS_GET,
  getPlatforms: CAPS.V2_PLATFORMS_GET,
};

export const OnboardingConfiguration = compose<EnhancedComponentProps, Record<string, never>>(
  clientComponents.withPromiseBasedCapabilities({
    ...INIT_CAPS,
    getProducts: CAPS.V2_PRODUCTS_GET,
  }),
  connect(state => ({
    capsFinished: capabilities.hasFinished(INIT_CAPS)(state),
    countries: getCountries(state),
    countryConfig: getCountryConfig(state),
    regionConfig: getRegionConfig(state),
    allPlatforms: getAllPlatforms(state),
  })),
  withLifecycle({
    async onWillMount({ getOptions, getPlatforms }) {
      await Promise.all([getOptions(), getPlatforms()]);
    },
  }),
  branch(
    // @ts-expect-error: FIXME
    ({ capsFinished }) => !capsFinished,
    renderComponent(() => <Loader />),
  ),
)(Component);
